import { c as _c } from "react/compiler-runtime";
import React, { useState, useCallback, useEffect, useMemo, useId, useRef } from 'react';
import * as datePicker from '@zag-js/date-picker';
import { useMachine, normalizeProps } from '@zag-js/react';
import { type CalendarDate } from '@internationalized/date';
import { isSameDay, isWithinInterval, isAfter, isBefore } from 'date-fns';
import type { DateRange, DatePickerV2Props, GetDateRangeFromPreset, RangeMode, SingleDateMode } from './types';
import { CUSTOM_PRESET_VALUE } from './constants';
import { canGrowMonthWindowBackward, canGrowMonthWindowForward, fromCalendarDate, growMonthWindow, MONTHS_PER_EXTENSION, MONTHS_TO_DISPLAY, resolveMonthWindow, resolveRangeSelection, toCalendarDate } from './utils';
export function useSingleDateSelection(t0) {
  const $ = _c(4);
  const initialDate = t0 === undefined ? null : t0;
  const [selectedDate, setSelectedDate] = useState(initialDate);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = date => {
      setSelectedDate(date);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const selectDate = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setSelectedDate(null);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== selectedDate) {
    t3 = {
      selectedDate,
      selectDate,
      clear: t2
    };
    $[2] = selectedDate;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
export function useSingleDateWithComparison(t0, t1) {
  const $ = _c(6);
  const initialDate = t0 === undefined ? null : t0;
  const initialComparisonDate = t1 === undefined ? null : t1;
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [comparisonDate, setComparisonDate] = useState(initialComparisonDate);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = date => {
      setSelectedDate(date);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const selectDate = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = date_0 => {
      setComparisonDate(date_0);
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const selectComparisonDate = t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setSelectedDate(null);
      setComparisonDate(null);
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== comparisonDate || $[4] !== selectedDate) {
    t5 = {
      selectedDate,
      comparisonDate,
      selectDate,
      selectComparisonDate,
      clear: t4
    };
    $[3] = comparisonDate;
    $[4] = selectedDate;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
export function useDateRangeSelection(t0) {
  const $ = _c(18);
  const initialRange = t0 === undefined ? null : t0;
  const [range, setRange] = useState(initialRange);
  const [tempStart, setTempStart] = useState(null);
  let t1;
  if ($[0] !== range || $[1] !== tempStart) {
    t1 = date => {
      if (tempStart === null) {
        setTempStart(date);
        if (range !== null) {
          setRange(null);
        }
        return null;
      }
      const newRange = isAfter(date, tempStart) || isSameDay(date, tempStart) ? {
        from: tempStart,
        to: date
      } : {
        from: date,
        to: tempStart
      };
      setRange(newRange);
      setTempStart(null);
      return newRange;
    };
    $[0] = range;
    $[1] = tempStart;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selectDateRange = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setRange(null);
      setTempStart(null);
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const clearRange = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setTempStart(null);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const clearTempStart = t3;
  let t4;
  if ($[5] !== range) {
    t4 = date_0 => {
      if (range == null) {
        return false;
      }
      return isWithinInterval(date_0, {
        start: range.from,
        end: range.to ?? range.from
      });
    };
    $[5] = range;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const isDateInRange = t4;
  let t5;
  if ($[7] !== range) {
    t5 = date_1 => range != null ? isSameDay(date_1, range.from) : false;
    $[7] = range;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const isRangeStart = t5;
  let t6;
  if ($[9] !== range) {
    t6 = date_2 => range?.to != null ? isSameDay(date_2, range.to) : false;
    $[9] = range;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const isRangeEnd = t6;
  let t7;
  if ($[11] !== isDateInRange || $[12] !== isRangeEnd || $[13] !== isRangeStart || $[14] !== range || $[15] !== selectDateRange || $[16] !== tempStart) {
    t7 = {
      range,
      tempStart,
      selectDateRange,
      clearRange,
      clearTempStart,
      setRange,
      isDateInRange,
      isRangeStart,
      isRangeEnd
    };
    $[11] = isDateInRange;
    $[12] = isRangeEnd;
    $[13] = isRangeStart;
    $[14] = range;
    $[15] = selectDateRange;
    $[16] = tempStart;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
export function useDateRangeWithComparison(t0, t1) {
  const $ = _c(19);
  const initialRange = t0 === undefined ? null : t0;
  const initialComparisonRange = t1 === undefined ? null : t1;
  const mainRange = useDateRangeSelection(initialRange);
  const comparisonRange = useDateRangeSelection(initialComparisonRange);
  let t2;
  if ($[0] !== comparisonRange.range || $[1] !== mainRange.range) {
    t2 = date => {
      const main = mainRange.range;
      const comparison = comparisonRange.range;
      if (main == null || comparison == null) {
        return false;
      }
      const mainEnd = main.to ?? main.from;
      const compEnd = comparison.to ?? comparison.from;
      const overlapStart = isAfter(main.from, comparison.from) ? main.from : comparison.from;
      const overlapEnd = isBefore(mainEnd, compEnd) ? mainEnd : compEnd;
      if (isAfter(overlapStart, overlapEnd)) {
        return false;
      }
      return isWithinInterval(date, {
        start: overlapStart,
        end: overlapEnd
      });
    };
    $[0] = comparisonRange.range;
    $[1] = mainRange.range;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isDateInOverlap = t2;
  let t3;
  if ($[3] !== comparisonRange.range || $[4] !== mainRange.range) {
    t3 = date_0 => {
      const main_0 = mainRange.range;
      const comparison_0 = comparisonRange.range;
      if (main_0 == null || comparison_0 == null) {
        return false;
      }
      const mainEnd_0 = main_0.to ?? main_0.from;
      const compEnd_0 = comparison_0.to ?? comparison_0.from;
      const overlapStart_0 = isAfter(main_0.from, comparison_0.from) ? main_0.from : comparison_0.from;
      const overlapEnd_0 = isBefore(mainEnd_0, compEnd_0) ? mainEnd_0 : compEnd_0;
      if (isAfter(overlapStart_0, overlapEnd_0)) {
        return false;
      }
      return isSameDay(date_0, overlapStart_0);
    };
    $[3] = comparisonRange.range;
    $[4] = mainRange.range;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const isOverlapStart = t3;
  let t4;
  if ($[6] !== comparisonRange.range || $[7] !== mainRange.range) {
    t4 = date_1 => {
      const main_1 = mainRange.range;
      const comparison_1 = comparisonRange.range;
      if (main_1 == null || comparison_1 == null) {
        return false;
      }
      const mainEnd_1 = main_1.to ?? main_1.from;
      const compEnd_1 = comparison_1.to ?? comparison_1.from;
      const overlapStart_1 = isAfter(main_1.from, comparison_1.from) ? main_1.from : comparison_1.from;
      const overlapEnd_1 = isBefore(mainEnd_1, compEnd_1) ? mainEnd_1 : compEnd_1;
      if (isAfter(overlapStart_1, overlapEnd_1)) {
        return false;
      }
      return isSameDay(date_1, overlapEnd_1);
    };
    $[6] = comparisonRange.range;
    $[7] = mainRange.range;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const isOverlapEnd = t4;
  let t5;
  if ($[9] !== comparisonRange || $[10] !== mainRange) {
    t5 = () => {
      mainRange.clearRange();
      comparisonRange.clearRange();
    };
    $[9] = comparisonRange;
    $[10] = mainRange;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== comparisonRange || $[13] !== isDateInOverlap || $[14] !== isOverlapEnd || $[15] !== isOverlapStart || $[16] !== mainRange || $[17] !== t5) {
    t6 = {
      mainRange,
      comparisonRange,
      isDateInOverlap,
      isOverlapStart,
      isOverlapEnd,
      clear: t5
    };
    $[12] = comparisonRange;
    $[13] = isDateInOverlap;
    $[14] = isOverlapEnd;
    $[15] = isOverlapStart;
    $[16] = mainRange;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}
export function useDateRangeWithOverlap(t0, t1) {
  const initialRange = t0 === undefined ? null : t0;
  const initialComparisonRange = t1 === undefined ? null : t1;
  return useDateRangeWithComparison(initialRange, initialComparisonRange);
}

/**
 * Mouse-drag range selection. Listens for mousedown on a date tile, tracks the
 * currently-hovered tile via mouseenter, and commits a range on mouseup. A
 * mouseup without movement is treated as a click and left to the existing
 * click handler. When the user did drag, the trailing click event is
 * suppressed so the existing click-based selection logic doesn't double-fire.
 */
export function useDragRangeSelection(opts: {
  enabled: boolean;
  onCommit: (range: DateRange) => void;
}): {
  dragRange: {
    start: Date;
    current: Date;
  } | null;
  onMouseDown: (date: Date) => void;
  onMouseEnter: (date: Date) => void;
} {
  const [dragRange, setDragRange] = useState<{
    start: Date;
    current: Date;
  } | null>(null);
  const dragMovedRef = useRef(false);
  const dragRangeRef = useRef<{
    start: Date;
    current: Date;
  } | null>(null);
  dragRangeRef.current = dragRange;
  const onCommitRef = useRef(opts.onCommit);
  onCommitRef.current = opts.onCommit;
  const isDragging = dragRange != null;
  useEffect(() => {
    if (!isDragging) return undefined;
    const handleUp = (): void => {
      const current = dragRangeRef.current;
      if (current != null && dragMovedRef.current) {
        const {
          start,
          current: end
        } = current;
        const from = isAfter(end, start) ? start : end;
        const to = isAfter(end, start) ? end : start;
        onCommitRef.current({
          from,
          to
        });
        // The trailing `click` would re-trigger the click-based selection
        // handler (Zag for `range`, our hook for comparison modes). Capture
        // it once and drop it before it bubbles.
        const suppressClick = (e: MouseEvent): void => {
          e.stopPropagation();
          e.preventDefault();
          globalThis.removeEventListener('click', suppressClick, true);
        };
        globalThis.addEventListener('click', suppressClick, true);
        // Safety: if no click follows (release outside the document), drop the
        // listener on the next tick so it doesn't eat an unrelated click later.
        setTimeout(() => {
          globalThis.removeEventListener('click', suppressClick, true);
        }, 0);
      }
      dragMovedRef.current = false;
      setDragRange(null);
    };
    globalThis.addEventListener('mouseup', handleUp);
    return () => {
      globalThis.removeEventListener('mouseup', handleUp);
    };
  }, [isDragging]);
  const onMouseDown = useCallback((date: Date): void => {
    if (!opts.enabled) return;
    dragMovedRef.current = false;
    setDragRange({
      start: date,
      current: date
    });
  }, [opts.enabled]);
  const onMouseEnter = useCallback((date_0: Date): void => {
    setDragRange(prev => {
      if (prev == null) return null;
      if (!isSameDay(date_0, prev.start)) dragMovedRef.current = true;
      if (isSameDay(date_0, prev.current)) return prev;
      return {
        start: prev.start,
        current: date_0
      };
    });
  }, []);
  return {
    dragRange,
    onMouseDown,
    onMouseEnter
  };
}
interface MonthWindowGrowth {
  /** Identity of the base window the growth was accumulated against. */
  key: string;
  before: number;
  after: number;
}
const NO_GROWTH: MonthWindowGrowth = {
  key: '',
  before: 0,
  after: 0
};

/**
 * The months rendered in the calendar, growable in both directions.
 *
 * The calendar has no month/year navigation controls, so the rendered window is
 * the effective selectable range. Seeding it with a fixed window around the anchor
 * and never growing it leaves earlier dates unreachable, so callers extend the
 * window as the user scrolls towards either edge. Growth is discarded whenever the
 * base window changes (e.g. the picker reopens on a different anchor).
 */
export function useMonthsToRender(anchor: Date, minDate?: Date, maxDate?: Date, mustInclude: Array<Date | undefined> = []): {
  months: Date[];
  anchorIndex: number;
  extendBackward: () => void;
  extendForward: () => void;
  canExtendBackward: boolean;
  canExtendForward: boolean;
} {
  const anchorTime = anchor.getTime();
  const minTime = minDate?.getTime();
  const maxTime = maxDate?.getTime();
  // Serialise the must-include dates to primitive timestamps so new Date
  // instances with the same value don't churn the memo.
  const mustIncludeKey = mustInclude.map(d => d?.getTime() ?? 'x').join(',');
  const baseWindow = useMemo(() => resolveMonthWindow(anchor, minDate, maxDate, MONTHS_TO_DISPLAY, mustInclude),
  // Compare by primitive timestamps so new Date instances with the same value don't churn the memo.

  [anchorTime, minTime, maxTime, mustIncludeKey]);

  // Deliberately excludes `mustInclude`: that only stretches the base window, and
  // it changes mid-selection (the comparison range moves with the primary range).
  // Discarding scroll-loaded months at that point would yank the calendar view.
  const growthKey = `${anchorTime}|${minTime ?? 'x'}|${maxTime ?? 'x'}`;
  const [growth, setGrowth] = useState<MonthWindowGrowth>(NO_GROWTH);
  // Derive rather than reset in an effect: a stale key means the base window moved
  // on and any accumulated growth no longer applies.
  const activeGrowth = growth.key === growthKey ? growth : NO_GROWTH;
  const monthWindow = useMemo(() => growMonthWindow(baseWindow, activeGrowth.before, activeGrowth.after, minDate, maxDate),
  // Compare by primitive timestamps so new Date instances with the same value don't churn the memo.

  [baseWindow, activeGrowth.before, activeGrowth.after, minTime, maxTime]);
  const extendBackward = useCallback((): void => {
    setGrowth(prev => {
      const base = prev.key === growthKey ? prev : NO_GROWTH;
      return {
        key: growthKey,
        before: base.before + MONTHS_PER_EXTENSION,
        after: base.after
      };
    });
  }, [growthKey]);
  const extendForward = useCallback((): void => {
    setGrowth(prev_0 => {
      const base_0 = prev_0.key === growthKey ? prev_0 : NO_GROWTH;
      return {
        key: growthKey,
        before: base_0.before,
        after: base_0.after + MONTHS_PER_EXTENSION
      };
    });
  }, [growthKey]);
  return {
    months: monthWindow.months,
    anchorIndex: monthWindow.anchorIndex,
    extendBackward,
    extendForward,
    canExtendBackward: canGrowMonthWindowBackward(monthWindow, minDate),
    canExtendForward: canGrowMonthWindowForward(monthWindow, maxDate)
  };
}
type ZagDatePickerService = Parameters<typeof datePicker.connect>[0];
export function useZagDatePicker(props: DatePickerV2Props, pendingRangePresetRef?: React.MutableRefObject<string | null>, getDateRangeFromPreset?: GetDateRangeFromPreset): {
  zagApi: ReturnType<typeof datePicker.connect>;
  service: ZagDatePickerService;
  useZag: boolean;
} {
  const {
    mode,
    minDate,
    maxDate,
    disabled = false,
    firstDayOfWeek = 1,
    shouldDisableDate,
    defaultValue
  } = props;
  const useZag = mode === 'single' || mode === 'range';
  const propsRef = React.useRef(props);
  propsRef.current = props;
  const pendingPresetRef = React.useRef(pendingRangePresetRef);
  pendingPresetRef.current = pendingRangePresetRef;
  const pickerId = useId();
  const defaultZagValue = useMemo<CalendarDate[]>(() => {
    if (mode === 'single') {
      const p = props as SingleDateMode;
      if (p.selectedDate != null) return [toCalendarDate(p.selectedDate)];
      if (defaultValue != null) return [toCalendarDate(defaultValue)];
      return [];
    }
    if (mode === 'range') {
      const p_0 = props as RangeMode;
      const range = resolveRangeSelection(p_0.selectedRange, p_0.selectedPreset, getDateRangeFromPreset, maxDate, minDate).range;
      if (range == null) return [];
      const vals: CalendarDate[] = [toCalendarDate(range.from)];
      if (range.to != null) vals.push(toCalendarDate(range.to));
      return vals;
    }
    return [];
  }, []);
  const service = useMachine(datePicker.machine, {
    id: pickerId,
    selectionMode: mode === 'range' ? 'range' : 'single',
    defaultValue: defaultZagValue,
    disabled,
    outsideDaySelectable: true,
    min: minDate != null ? toCalendarDate(minDate) : undefined,
    max: maxDate != null ? toCalendarDate(maxDate) : undefined,
    startOfWeek: firstDayOfWeek,
    numOfMonths: MONTHS_TO_DISPLAY,
    inline: true,
    isDateUnavailable: shouldDisableDate != null ? date => shouldDisableDate(fromCalendarDate(date)) : undefined,
    onValueChange(details: datePicker.ValueChangeDetails) {
      const p_1 = propsRef.current;
      if (p_1.mode !== 'single' && p_1.mode !== 'range') return;
      const dates = details.value.map(fromCalendarDate);
      if (p_1.mode === 'single') {
        p_1.onSelectDate(dates[0] ?? null);
        return;
      }
      const presetRef = pendingPresetRef.current;
      const presetValue = presetRef?.current ?? CUSTOM_PRESET_VALUE;
      if (presetRef != null) presetRef.current = null;
      if (dates.length === 2) {
        p_1.onSelectRange({
          from: dates[0],
          to: dates[1]
        }, presetValue);
      } else if (dates.length === 0) {
        p_1.onSelectRange(null, CUSTOM_PRESET_VALUE);
      }
    }
  });
  const zagApi = datePicker.connect(service, normalizeProps);
  return {
    zagApi,
    service,
    useZag
  };
}